import { DuplicateCheck } from './duplicate-check';
import type { FileInputConfig, FileUploadIssue } from './models';
import { Selection, SelectionError } from './selection';
import { Validation } from './validation';

export type FileIntakeSource =
    | { kind: 'drop'; dataTransfer: DataTransfer }
    | { kind: 'files'; files: readonly File[] };

export interface FileIntakeOptions {
    source: FileIntakeSource;
    validation: FileInputConfig | false;
    comparisonFiles?: readonly File[];
    /** Preserve an already reviewed selection, or inspect raw inputs before admission. */
    deduplicate?: boolean;
    strictMaxRawFileCount?: number;
    signal?: AbortSignal;
}

export type FileIntakeResult =
    | {
          kind: 'ready';
          files: File[];
          issues: FileUploadIssue[];
          duplicateNames: string[];
      }
    | {
          kind: 'rejected';
          message: string;
          reason: 'count' | 'size' | 'empty' | 'read' | 'aborted';
          issues: FileUploadIssue[];
          duplicateNames: string[];
      };

/** Create within an injection context; returned methods can run outside it. */
export function injectFileIntake() {
    const selection = new Selection();
    const validation = new Validation();
    const duplicates = new DuplicateCheck();

    return {
        async prepare(options: FileIntakeOptions): Promise<FileIntakeResult> {
            const rejected = (
                reason: Extract<
                    FileIntakeResult,
                    { kind: 'rejected' }
                >['reason'],
                message: string,
                issues: FileUploadIssue[] = [],
                duplicateNames: string[] = []
            ): FileIntakeResult => ({
                kind: 'rejected',
                reason,
                message,
                issues,
                duplicateNames,
            });
            try {
                if (options.signal?.aborted)
                    throw new SelectionError('aborted');
                const files =
                    options.source.kind === 'drop'
                        ? await selection.extractFilesFromDrop(
                              options.source.dataTransfer,
                              options
                          )
                        : Array.from(options.source.files);
                if (options.signal?.aborted)
                    throw new SelectionError('aborted');
                if (
                    options.strictMaxRawFileCount !== undefined &&
                    files.length > options.strictMaxRawFileCount
                ) {
                    throw new SelectionError('count');
                }
                const { uniqueFiles, duplicateNames } =
                    options.deduplicate === false
                        ? { uniqueFiles: files, duplicateNames: [] }
                        : duplicates.filterDuplicates(
                              files,
                              options.comparisonFiles
                          );
                if (!uniqueFiles.length) {
                    return rejected(
                        'empty',
                        'No new files to upload.',
                        [],
                        duplicateNames
                    );
                }
                if (options.validation === false) {
                    return {
                        kind: 'ready',
                        files: uniqueFiles,
                        issues: [],
                        duplicateNames,
                    };
                }
                const result = validation.validateFiles(
                    uniqueFiles,
                    options.validation
                );
                if (result.hasBlockingIssue || !result.acceptedFiles.length) {
                    return rejected(
                        result.hasBlockingIssue &&
                            result.issues.some((issue) =>
                                issue.title.includes('size')
                            )
                            ? 'size'
                            : 'empty',
                        result.issues[0]?.message ??
                            'No files can be uploaded.',
                        result.issues,
                        duplicateNames
                    );
                }
                return {
                    kind: 'ready',
                    files: result.acceptedFiles,
                    issues: result.issues,
                    duplicateNames,
                };
            } catch (error) {
                const reason =
                    error instanceof SelectionError ? error.reason : 'read';
                const message =
                    reason === 'count'
                        ? `Choose up to ${options.strictMaxRawFileCount} file${options.strictMaxRawFileCount === 1 ? '' : 's'}. Nothing was uploaded.`
                        : reason === 'aborted'
                          ? 'File selection cancelled.'
                          : 'Some files could not be read. Nothing was uploaded.';
                return rejected(reason, message);
            }
        },
    };
}
